import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getYear } from "date-fns";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import { PP_CORRECTION_REASON_OPTIONS } from "features/cdm/constants/correctionReasonOptions";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { formatEircode, validateEircode } from "utils/validators";

import { diagnosisItems } from "../../constants/diagnosisItems";
import { getVisiblePpReasons } from "../../utils/visibleRegistrationReasons";
import style from "../AddPPReviewDialog/AddPPReviewDialog.module.css";

const buildEditSchema = (isCorrection) =>
  z.object({
  consultationDate: z.string().min(1, { message: "Please provide consultation date." }),
  eircode: z
    .string()
    .min(1, { message: "Please provide eircode." })
    .refine(
      (val) => validateEircode(val).isValid,
      (val) => ({ message: validateEircode(val).error || "Invalid Eircode format" })
    ),
  ethnicityCode: z.string().optional(),
  contractDoctor: z.string().optional(),
  correctionReason: isCorrection
    ? z.string().min(1, { message: "Please select a reason for correction." })
    : z.string().optional(),
  qrisk3: z.boolean().optional().default(false),
  qrisk3Year: z.string().optional(),
  hypertenstionStage1: z.boolean().optional().default(false),
  hypertenstionStage1Year: z.string().optional(),
  hypertenstionStage1NoTargetOrganDamage: z.boolean().optional().default(false),
  hypertenstionStage1NoTargetOrganDamageYear: z.string().optional(),
  hypertenstionStage2: z.boolean().optional().default(false),
  hypertenstionStage2Year: z.string().optional(),
  preEclampsia: z.boolean().optional().default(false),
  preEclampsiaYear: z.string().optional(),
  preDiabetes: z.boolean().optional().default(false),
  preDiabetesYear: z.string().optional(),
  previousBNP: z.boolean().optional().default(false),
  previousBNPYear: z.string().optional(),
});

export const PpInitialStep = ({ data, onContinue, onCancel, submitDisabled = false }) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: patient } = usePatientData();
  const { data: healthIdentifiers } = usePatientHealthIdentifiers();
  const { data: ethnicGroups } = useEthnicGroups();

  // Editing an already-submitted review is a correction (HL7 OBR.25=C).
  const isCorrection = !!data?.reimbursementMessageControlId;

  // Reasons already on the review stay visible on edit so a value is never lost.
  const reasonItems = getVisiblePpReasons({
    patient,
    haaNumber: healthIdentifiers?.haaNumber,
    alreadySelectedIds: data?.registrationReasons ?? [],
  });

  const currentYear = new Date().getFullYear().toString();

  const diagnosisDefaults = diagnosisItems.reduce((acc, item) => {
    const isChecked = (data?.registrationReasons ?? []).includes(item.id);
    return {
      ...acc,
      [item.field]: isChecked,
      [`${item.field}Year`]: data?.yearOfRegistration ?? currentYear,
    };
  }, {});

  const form = useForm({
    resolver: zodResolver(buildEditSchema(isCorrection)),
    defaultValues: {
      consultationDate: data?.consultationDate ?? "",
      eircode: data?.eircode ?? "",
      contractDoctor: "",
      ethnicityCode: data?.ethnicityCode ?? "",
      correctionReason: data?.correctionReason ?? "",
      ...diagnosisDefaults,
    },
  });

  useEffect(() => {
    // On edit keep the eircode/ethnicity submitted with the previous review;
    // only fall back to the patient record for a new review (no saved value).
    if (!data?.eircode && patient?.address?.postCode) {
      form.setValue("eircode", formatEircode(patient.address.postCode) || patient.address.postCode);
    }
    if (!data?.ethnicityCode && patient?.ethnicityCode) {
      form.setValue("ethnicityCode", patient.ethnicityCode);
    }
  }, [
    data?.eircode,
    data?.ethnicityCode,
    patient?.address?.postCode,
    patient?.ethnicityCode,
    form,
  ]);

  useEffect(() => {
    if (staffMembers && data?.reviewingDoctor) {
      const match = staffMembers.find(
        (s) => `${s.firstName} ${s.lastName}` === data.reviewingDoctor
      );
      if (match) {
        form.setValue("contractDoctor", String(match.id));
      }
    }
  }, [staffMembers, data?.reviewingDoctor, form]);

  const handleEircodeBlur = (event) => {
    const val = event.target.value;

    if (val) {
      form.setValue("eircode", formatEircode(val), { shouldValidate: true });
    }
  };

  const handleFormSubmit = (formData) => {
    const checkedItems = diagnosisItems.filter((item) => formData[item.field]);
    const checkedIds = checkedItems.map((item) => item.id);

    // A correction (HL7 OBR.25=C) must hold the original treatment date
    // (GP Contract spec, Enhancement 27). Consultation date is read-only here,
    // so always carry the original reviewDate — never recompute it.
    onContinue({
      registrationReasons: checkedIds,
      reviewDate: data?.reviewDate,
      consultationDate: data?.consultationDate ?? formData.consultationDate,
      ethnicityCode: formData.ethnicityCode,
      eircode: formData.eircode ? formData.eircode.replace(/\s+/g, "") : formData.eircode,
      contractDoctor: formData.contractDoctor,
      correctionReason: formData.correctionReason,
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={style.addReferralForm}
        style={{ borderTop: "none", paddingTop: 0, marginTop: 0, gap: 0 }}
        onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PpInitialStep"))}
      >
      <div style={{ backgroundColor: "var(--gray-50)", paddingTop: "24px", paddingBottom: "24px" }}>
        <div
          className={style.inputGroupGrid4Columns}
          style={{
            padding: "0 24px",
            ...(isCorrection && { gridTemplateColumns: "repeat(5, 1fr)" }),
          }}
        >
          {isCorrection && (
            <FormField name="correctionReason">
              <label className={style.addReferralFormLabel}>
                Reason for correction
              </label>
              <Select
                options={PP_CORRECTION_REASON_OPTIONS}
                placeholder="Select"
                value={form.watch("correctionReason") ?? ""}
                onChange={(value) =>
                  form.setValue("correctionReason", value, {
                    shouldValidate: true,
                  })
                }
              />
            </FormField>
          )}
          <FormField name="consultationDate">
            <label className={style.pregnancyFormLabel}>Consultation date</label>
            <input
              {...form.register("consultationDate")}
              type="text"
              className="form-control"
              placeholder="dd-MM-yyyy"
              readOnly
              disabled
              title="A correction keeps the original review date — it cannot be changed."
            />
          </FormField>
          <FormField name="contractDoctor">
            <label className={style.addReferralFormLabel}>Contract Doctor</label>
            <Select
              options={(staffMembers ?? []).map((staff) => ({
                value: staff.id,
                label: `${staff.firstName} ${staff.lastName}${staff.staffRole ? ` (${staff.staffRole.toLowerCase()})` : ""}${staff.active ? "" : " (inactive)"}`,
              }))}
              value={form.watch("contractDoctor") ?? ""}
              onChange={(value) => form.setValue("contractDoctor", value, { shouldValidate: true })}
            />
          </FormField>
          <FormField name="ethnicityCode">
            <label className={style.addReferralFormLabel}>Ethnicity</label>
            <Select
              options={(ethnicGroups ?? []).map((group) => group.description)}
              value={ethnicGroups?.find((g) => g.code === form.watch("ethnicityCode"))?.description ?? ""}
              onChange={(value) => {
                const selectedGroup = ethnicGroups?.find((g) => g.description === value);
                form.setValue("ethnicityCode", selectedGroup?.code ?? "", { shouldValidate: true });
              }}
            />
          </FormField>
          <FormField name="eircode">
            <label className={style.pregnancyFormLabel}>Eircode</label>
            <input
              type="text"
              className="form-control"
              {...form.register("eircode")}
              onBlur={handleEircodeBlur}
            />
          </FormField>
        </div>
        <div className={style.indicatorsSection} style={{ paddingLeft: "24px", paddingRight: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>Diagnosis</h2>
          <div className={style.tableContainer}>
            <table className={style.table}>
              <thead className={style.thead}>
                <tr>
                  <th className={style.th}></th>
                  <th className={style.th}>Reason</th>
                  <th className={style.th}>Year</th>
                </tr>
              </thead>
              <tbody>
                {reasonItems.map((item) => (
                  <tr key={item.id}>
                    <td className={style.tdName} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "40px" }}>
                      <CustomCheckbox
                        checked={form.watch(item.field)}
                        onChange={(event) => form.setValue(item.field, event.target.checked)}
                      />
                    </td>
                    <td title="Diagnosis" className={style.tdName} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "300px" }}>
                      {item.name}
                    </td>
                    <td title="Year" className={style.tdName} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "140px" }}>
                      <Select
                        small
                        options={Array.from({ length: 150 }, (_, i) => getYear(new Date()) - i).map((year) => ({
                          value: year,
                          label: year,
                        }))}
                        disabled={!form.watch(item.field)}
                        value={form.watch(`${item.field}Year`) ?? ""}
                        onChange={(value) => form.setValue(`${item.field}Year`, value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", padding: "24px", borderTop: "1px solid var(--gray-200)" }}>
        <Button variant="outline" type="button" onClick={onCancel} style={{ width: "200px" }}>Back</Button>
        <Button type="submit" disabled={submitDisabled} style={{ width: "200px" }}>Continue</Button>
      </div>
      </form>
    </FormProvider>
  );
};
